import * as React from 'react';
import { SignUp } from 'aws-amplify-react';

import { PasswordRequirements } from './password-requirements';
import { withSectionBodyNotes } from './section-body-notes';
import { renderStockScreen } from './stock-screen';

// Stated once at the top rather than repeated as a marker on all five labels.
// An asterisk only distinguishes anything on a form that mixes required and
// optional fields; here it would flag every label identically, which is exactly
// no help on Phone Number — the one field people expect to be optional.
const ALL_REQUIRED_NOTE = (
  <div className="ls-auth-hint ls-auth-intro-note" key="ls-all-required">
    All fields are required.
  </div>
);

// Notes injected directly beneath the sign-up field of the matching key. The
// keys are the `signUpFields` keys configured in SecureApp.tsx — `username` is
// the Email field, which doubles as the Cognito username.
const FIELD_NOTES: { [key: string]: React.ReactNode } = {
  // The permanence warning earns its place here: nothing in the app can change
  // an address once the account exists (no updateUserAttributes call anywhere,
  // and Settings has no email field), and a practitioner who signs up with a
  // school address loses the account when they graduate.
  //
  // School is named deliberately and business email is not — practitioners
  // routinely use a practice address and that is fine, so warning them off it
  // would be wrong advice.
  username: (
    <div className="ls-auth-hint ls-auth-field-note" key="ls-username-note">
      This becomes the sign-in username. It is <strong>case-sensitive</strong> and{' '}
      <strong>cannot be changed later</strong> — avoid an address that may stop working, such as a
      school email.
    </div>
  ),
  password: <PasswordRequirements className="ls-auth-field-note" key="ls-password-requirements" />,
};

/**
 * The stock Amplify v3 SignUp screen, plus the account guidance practitioners
 * need while they are creating the account: that the email address becomes
 * their case-sensitive username, and what the password policy is.
 */
export class LabSmartsSignUp extends SignUp {
  /**
   * Amplify drives both the " *" label marker and its submit check off the same
   * `required` flag, and the flags are turned off so the all-required form does
   * not carry asterisks that mark nothing (see signUpConfig). Validation moves
   * here instead: every field on this form must be filled in.
   */
  validate(): any[] {
    return this.signUpFields
      .filter(field => {
        const value =
          field.key === 'phone_number' ? this.phone_number : this.inputs[field.key];
        return !value;
      })
      .map(field => field.label);
  }

  showComponent(theme: any): React.ReactNode {
    return withSectionBodyNotes(
      renderStockScreen(this, SignUp, () => super.showComponent(theme)),
      'sign-up-body-section',
      fields => {
        // The base render emits exactly one element per configured field, in
        // the order `sortFields()` left them.
        if (fields.length !== this.signUpFields.length) {
          return null;
        }

        const annotated: React.ReactNode[] = [ALL_REQUIRED_NOTE];
        this.signUpFields.forEach((field, index) => {
          annotated.push(fields[index]);
          const note = FIELD_NOTES[field.key];
          if (note) {
            annotated.push(note);
          }
        });
        return annotated;
      },
    );
  }
}
